<template>
    <!-- If you're on Vue 3, replace <portal> with <teleport to="#modals"> -->
    <!-- Essa modal preve a abertura de varias modal simultaneas, em contrapartida ao MtwModal, que abre apenas uma-->
    <!-- Melhor para criacao de atributos e etc, para mostrar menssagens e tarefas mais simples melhor usar mtwmodal-->
    <portal to="modals">
        <transition name="modal-fade">
            <div v-if="isOpen" class="modal_overlay" :style="{ zIndex: zIndex }" role="dialog" aria-modal="true"
                :aria-labelledby="titleId" @keydown.esc.stop.prevent="handleEsc" @click="onOverlayClick">
                <div class="modal_content" ref="modal" :class="{ 'no-padding': nopadding, fullscreen: fullscreen }"
                    :style="contentStyle" tabindex="-1">
                    <!-- Header -->
                    <div class="modal_header">
                        <div class="modal_title" :id="titleId">
                            <!-- prefer 'header' like b-modal; fallback to 'title' -->
                            <slot name="header">
                                <slot name="title"></slot>
                            </slot>
                        </div>
                        <font-awesome-icon icon="times" class="close_icon" @click="requestClose" />
                    </div>

                    <!-- Body -->
                    <div class="modal_body">
                        <!-- prefer 'body' like b-modal; fallback to default slot -->
                        <slot name="body">
                            <slot />
                        </slot>
                    </div>

                    <!-- Footer -->
                    <div class="modal_footer">
                        <slot name="footer">
                            <button v-if="cancel" @click="$emit('cancel')" class="cancel_button">
                                {{ $t('cancel') }}
                            </button>
                            <button v-if="confirm" @click="$emit('confirm')" class="confirm_button">
                                {{ $t('confirm') }}
                            </button>
                        </slot>
                    </div>
                </div>
            </div>
        </transition>
    </portal>
</template>
  
<script>
export default {
    name: 'PlainModal',
    inject: ['$modalManager'],
    props: {
        /* visibility: supports both visible and v-model (modelValue) */
        visible: { type: Boolean, default: undefined },
        modelValue: { type: Boolean, default: undefined },

        /* buttons */
        confirm: Boolean,
        cancel: Boolean,

        /* sizing (keeps your old props; also accepts size/fullscreen/nopadding) */
        width: { type: String, default: 'auto' },
        height: { type: String, default: 'auto' },
        maxWidth: { type: String, default: '1200px' },
        maxHeight: { type: String, default: '80vh' },
        minWidth: { type: String, default: '50vw' },
        minHeight: { type: String, default: '100px' },
        size: { type: String, default: null },          // 'sm' | 'md' | 'lg' | 'xl'
        fullscreen: { type: Boolean, default: false },
        nopadding: { type: Boolean, default: false },

        /* behavior */
        closeOnEsc: { type: Boolean, default: true },
        closeOnBackdrop: { type: Boolean, default: true },
        zIndex: { type: Number, default: 1000 },
        id: { type: String, default: 'modal' }          // for a11y parity
    },
    emits: [
        'update:visible', 'update:modelValue',
        'show', 'shown', 'hide',
        'close', 'confirm', 'cancel'
    ],
    data() {
        return {
            titleId: `modal-title-${Math.random().toString(36).slice(2)}`,
            countedOpen: false, // <- track if THIS instance has incremented
            lastAppliedOpen: undefined,
        };
    },
    computed: {
        // unified source of truth for open state

        contentStyle() {
            // size shortcuts like b-modal
            const preset =
                this.size === 'sm' ? { maxWidth: '360px' } :
                    this.size === 'md' ? { maxWidth: '600px' } :
                        this.size === 'lg' ? { maxWidth: '900px' } :
                            this.size === 'xl' ? { maxWidth: '90vw' } : {};

            const explicit = {
                width: this.width,
                height: this.height,
                maxWidth: this.maxWidth,
                maxHeight: this.maxHeight,
                minWidth: this.minWidth,
                minHeight: this.minHeight
            };

            // fullscreen overrides
            if (this.fullscreen) {
                return {
                    width: '90vw',
                    height: 'max-content',
                    maxHeight: '90vh',
                    ...explicit
                };
            }
            return { ...preset, ...explicit };
        },
        isOpen() {
            // prefer modelValue if provided; else visible
            return (this.modelValue ?? this.visible) ?? false;
        },
    },
    watch: {
        isOpen: {
            immediate: true,
            handler(open) {
                if (open && !this.countedOpen) {
                    this.countedOpen = true;
                    this.$modalManager?.increment();
                    this.$emit('show');
                    this.lockScroll(true);
                    this.$nextTick(() => { this.focusModal(); this.$emit('shown'); });
                    return;
                }

                if (!open && this.countedOpen) {
                    this.countedOpen = false;
                    this.$modalManager?.decrement();
                    this.lockScroll(false);
                    this.$emit('hide');
                    return;
                }

            }
        }
    },
    mounted() {
        if (this.isOpen) this.focusModal();
    },
    beforeDestroy() {
        if (this.countedOpen) {
            this.$modalManager?.decrement();
            this.countedOpen = false;
        }
        this.lockScroll(false);
    },
    methods: {
        setOpen(val) {
            this.$emit('update:modelValue', val);
            this.$emit('update:visible', val);
            if (!val) this.$emit('close');
        },
        requestClose() {
            this.setOpen(false);
        },
        handleEsc() {
            if (this.closeOnEsc) this.requestClose();
        },
        onOverlayClick() {
            //if (!this.closeOnBackdrop) return;
            //if (e.target === e.currentTarget) this.requestClose();
        },
        lockScroll(lock) {
            const cls = 'modal-open-scroll-lock';
            if (lock) document.body.classList.add(cls);
            else document.body.classList.remove(cls);
        },
        focusModal() {
            const el = this.$refs.modal;
            if (!el) return;
            // try to focus first focusable; fallback to container
            const focusable = el.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            (focusable || el).focus({ preventScroll: true });
        },
    }
};
</script>
  
<style scoped>
/* === Transitions === */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity .15s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

/* === Overlay === */
.modal_overlay {
    position: fixed;
    inset: 0;
    background: rgba(var(--tmHubTheme-darken-primary-color), 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
}

/* === Modal Container === */
.modal_content {
    background: transparent;
    /* transparent so header/body/footer define bands */
    padding: 0;
    /* remove outer padding */
    border-radius: 0.5rem;
    /* tighter radius */
    overflow: hidden;
    color: #fff;
    display: flex;
    flex-direction: column;
    outline: none;
    box-shadow: rgba(0, 0, 0, .56) 0 22px 70px 4px;
    border: 1px solid #dee2e6;
    border-color: hsla(0, 0%, 100%, .3);
}

.modal_content.no-padding .modal_body {
    padding: 0 !important;
}

/* === Header === */
.modal_header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0;
    /* no extra gap */
    padding: 0.75rem 1rem;
    background-color: rgb(var(--tmHubTheme-darken-primary-color));
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    border-bottom: 1px solid #dee2e6;
    border-color: hsla(0, 0%, 100%, .3);
}

.modal_title {
    height: auto;
    font-size: 1.1rem;
    font-weight: 600;
}

.close_icon {
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    cursor: pointer;
    font-size: 1.1rem;
    color: #cfd8dc;
}

.close_icon:hover {
    color: #ff6b6b;
}

/* === Body === */
.modal_body {
    flex: 1 1 auto;
    padding: 1rem 1.25rem;
    background-color: #1c2734;
    background-color: rgb(var(--tmHubTheme-lighten-primary-color));
    overflow-y: auto;
    max-height: none;
    min-height: 120px;
}

/* === Footer === */
.modal_footer {
    flex: 0 0 auto;
    margin-top: 0;
    padding: 0.75rem 1rem;
    background-color: #121c26;
    background-color: rgb(var(--tmHubTheme-darken-primary-color));
    border-top: 1px solid rgba(255, 255, 255, .15);
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    display: flex;
    justify-content: flex-end;
    gap: .5rem;
    border-top: 1px solid #dee2e6;
    border-color: hsla(0, 0%, 100%, .3);
}

/* === Buttons === */
.cancel_button {
    padding: .5rem 1rem;
    background: #ef4444;
    color: white;
    border: none;
    border-radius: .25rem;
    cursor: pointer;
}

.cancel_button:hover {
    background: #dc2626;
}

.confirm_button {
    padding: .5rem 1rem;
    background: #44ef44;
    color: white;
    border: none;
    border-radius: .25rem;
    cursor: pointer;
}

.confirm_button:hover {
    background: #26dc26;
}

/* === Fullscreen Mode === */
.modal_content.fullscreen {
    width: 90vw;
    max-width: 90vw;
    max-height: 90vh;
    border-radius: 0.75rem;
}

/* === Inputs inside modal (optional) === */
.modal_body input,
.modal_body select,
.modal_body textarea {
    background: transparent;
    color: #e9eef3;
    border: 1px solid rgba(255, 255, 255, .35);
}

.modal_body input:focus,
.modal_body select:focus,
.modal_body textarea:focus {
    border-color: rgba(255, 255, 255, .6);
    box-shadow: none;
}
</style>
  
<style>
/* scroll lock class applied to <body> */
.modal-open-scroll-lock {
    overflow: hidden !important;
}
</style>
  